import { VENDOR_AGGREGATION_GROUP } from "@dransay/api/models";
import { useCallback, useEffect, useRef } from "react";
import { useSnackbar } from "~/components/snackbar/use-snackbar.hook";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import { filterWarningEvent } from "~/tooling/telemetry/events";
import type { FiltersConfig } from "../filters/types";
import type { FilterSetter } from "../filters/types";
import { getTopProducerIds } from "../filters/use-filters.hook";

const PHARMACY_AUTO_DISMISS_MS = 7_000;

interface UseFilterWarningsOptions {
	filterConfig: FiltersConfig;
	setFilter: FilterSetter;
	selectedVendorId?: string;
	selectedDeliveryMethodKind?: string;
	setSelectedVendor: (vendorId?: string) => Promise<void>;
}

/** Manages manufacturer and pharmacy warning snackbars with telemetry. */
export function useFilterWarnings({
	filterConfig,
	setFilter,
	selectedVendorId,
	selectedDeliveryMethodKind,
	setSelectedVendor,
}: UseFilterWarningsOptions) {
	const manufacturerSnackbar = useSnackbar({
		sessionKey: "da-mkp346:mfg-warning",
	});

	const pharmacySnackbar = useSnackbar({
		sessionKey: "da-mkp881:pharm-warning",
		autoDismissMs: PHARMACY_AUTO_DISMISS_MS,
		isRetriggerable: true,
	});

	const showManufacturerWarning = useCallback(() => {
		const wasShown = manufacturerSnackbar.show();
		if (wasShown) {
			captureEvent(filterWarningEvent("manufacturer", "shown"));
		}
	}, [manufacturerSnackbar.show]);

	const undoManufacturerWarning = useCallback(() => {
		setFilter("topProducersLowPrice", true);
		setFilter("producers", getTopProducerIds(filterConfig));
		manufacturerSnackbar.dismiss();
		captureEvent(filterWarningEvent("manufacturer", "undo"));
	}, [setFilter, filterConfig, manufacturerSnackbar.dismiss]);

	const dismissManufacturerWarning = useCallback(() => {
		manufacturerSnackbar.dismiss();
		captureEvent(filterWarningEvent("manufacturer", "dismissed"));
	}, [manufacturerSnackbar.dismiss]);

	const previousVendorIdRef = useRef(selectedVendorId);
	const previousDeliveryMethodRef = useRef(selectedDeliveryMethodKind);

	useEffect(() => {
		const wasTop = previousVendorIdRef.current === VENDOR_AGGREGATION_GROUP.TOP;
		const isAll = selectedVendorId === VENDOR_AGGREGATION_GROUP.ALL;
		const deliveryMethodChanged =
			previousDeliveryMethodRef.current !== selectedDeliveryMethodKind;

		if (wasTop && isAll && !deliveryMethodChanged) {
			const wasShown = pharmacySnackbar.show();
			if (wasShown) {
				captureEvent(filterWarningEvent("pharmacy", "shown"));
			}
		}

		previousVendorIdRef.current = selectedVendorId;
		previousDeliveryMethodRef.current = selectedDeliveryMethodKind;
	}, [selectedVendorId, selectedDeliveryMethodKind, pharmacySnackbar.show]);

	const undoPharmacyWarning = useCallback(() => {
		void setSelectedVendor(VENDOR_AGGREGATION_GROUP.TOP)
			.then(() => {
				pharmacySnackbar.dismiss();
				captureEvent(filterWarningEvent("pharmacy", "undo"));
			})
			.catch(() => {
				// Keep the snackbar visible so user can retry.
			});
	}, [setSelectedVendor, pharmacySnackbar.dismiss]);

	const dismissPharmacyWarning = useCallback(() => {
		pharmacySnackbar.dismiss();
		captureEvent(filterWarningEvent("pharmacy", "dismissed"));
	}, [pharmacySnackbar.dismiss]);

	return {
		manufacturer: {
			isVisible: manufacturerSnackbar.isVisible,
			show: showManufacturerWarning,
			undo: undoManufacturerWarning,
			dismiss: dismissManufacturerWarning,
		},
		pharmacy: {
			isVisible: pharmacySnackbar.isVisible,
			undo: undoPharmacyWarning,
			dismiss: dismissPharmacyWarning,
			pauseAutoDismiss: pharmacySnackbar.pauseAutoDismiss,
			resumeAutoDismiss: pharmacySnackbar.resumeAutoDismiss,
		},
	};
}
